import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useTeam } from "@/context/TeamContext";
import { SoccerBall, UsersThree, ShieldCheck } from "@phosphor-icons/react";

// Module-scoped so framer-motion isn't handed a brand new object literal
// (and therefore treated as a changed prop) on every render of this page.
const STAGGER_ROW_INITIAL = { opacity: 0, y: 6 };
const STAGGER_ROW_ANIMATE = { opacity: 1, y: 0 };


// Read-only for everyone — Add/Edit/Delete Player now lives on the Admin
// page's Roster tab (Admin-tier only). This page just lets any role
// (Admin/Member/Viewer) browse the squad.
export default function Roster() {
  const navigate = useNavigate();
  const { currentTeam, currentTeamId, teams, currentRole, loading: teamsLoading } = useTeam();
  const isOwner = currentRole === "owner" || currentRole === "admin";
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!currentTeamId) return;
    try {
      const { data } = await api.get(`/teams/${currentTeamId}/players`);
      setPlayers(data);
    } finally {
      setLoading(false);
    }
  }, [currentTeamId]);

  useEffect(() => {
    if (teamsLoading) return;
    if (teams.length === 0) {
      navigate("/setup");
      return;
    }
    if (currentTeamId) {
      setLoading(true);
      load();
    }
  }, [teamsLoading, teams.length, currentTeamId, load, navigate]);

  return (
    <Layout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="font-label text-xs text-[#CCFF00] mb-1">{currentTeam?.name} · {players.length} players</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-white leading-none">Roster</h1>
        </div>
        {isOwner && (
          <button
            onClick={() => navigate("/admin?tab=roster")}
            data-testid="manage-roster-in-admin-btn"
            className="flex items-center gap-2 border border-zinc-800 text-zinc-300 hover:text-[#CCFF00] hover:border-[#CCFF00] font-label text-xs px-4 py-3 rounded-sm transition-colors duration-150"
          >
            <ShieldCheck size={16} weight="bold" /> Manage in Admin
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-32">
          <SoccerBall size={40} className="text-[#CCFF00] animate-spin" weight="fill" />
        </div>
      ) : players.length === 0 ? (
        <div className="bg-[#121212] border border-dashed border-zinc-800 rounded-sm py-20 flex flex-col items-center" data-testid="roster-empty">
          <UsersThree size={40} className="text-zinc-700 mb-4" />
          <p className="text-zinc-400 mb-1 font-heading text-2xl">No players yet</p>
          <p className="text-zinc-600 text-sm">{isOwner ? "Add your first player from the Admin page." : "No players on the roster yet."}</p>
        </div>
      ) : (
        <div className="border border-zinc-800 rounded-sm overflow-hidden" data-testid="roster-table">
          <div className="grid grid-cols-[60px_1fr_1fr_100px] gap-4 px-4 py-3 bg-[#121212] border-b border-zinc-800">
            <span className="font-label text-[10px] text-zinc-500">#</span>
            <span className="font-label text-[10px] text-zinc-500">Name</span>
            <span className="font-label text-[10px] text-zinc-500 hidden sm:block">Position</span>
            <span className="font-label text-[10px] text-zinc-500">Status</span>
          </div>
          {players.map((p, i) => (
            <motion.div
              key={p.id}
              initial={STAGGER_ROW_INITIAL}
              animate={STAGGER_ROW_ANIMATE}
              transition={{ delay: i * 0.03 }}
              className="grid grid-cols-[60px_1fr_1fr_100px] gap-4 px-4 py-3 items-center border-b border-zinc-900 hover:bg-white/5 transition-colors duration-150"
              data-testid={`player-row-${p.jersey_number}`}
            >
              <span className="font-heading text-2xl text-[#CCFF00]">{p.jersey_number}</span>
              <div>
                <p className="text-white font-semibold">{p.first_name} {p.last_name}</p>
                <p className="text-zinc-500 text-xs sm:hidden">{p.position || "—"}</p>
              </div>
              <span className="text-zinc-300 text-sm hidden sm:block">{p.position || <span className="text-zinc-600">—</span>}</span>
              <span
                data-testid={`player-status-${p.jersey_number}`}
                className={`font-label text-[10px] px-2 py-1 rounded-sm w-fit ${p.active ? "text-[#CCFF00] bg-[#CCFF00]/10" : "text-zinc-500 bg-zinc-800/50"}`}
              >
                {p.active ? "Active" : "Inactive"}
              </span>
            </motion.div>
          ))}
        </div>
      )}
    </Layout>
  );
}
